/* global module*/

import React from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';
import Slider from '../../common/Slider/index.tsx';
import NavButtons from '../../common/NavButtons.tsx';
import HomeSize from './HomeSize';
import MultiUnitInput from './MultiUnitInput';
import Help from './Help';
import CommonHelp from '../../common/Help';
import { wrap, withT } from '../../../lib/wrappers';
import { userApiValue, defaultApiValue } from '../../../lib/legacyHelpers';
import selectors, { bindSelectors, pick } from '../../../redux/selectors';
import a, { bindActionCreators } from '../../../redux/actions';
import ResizableComponent from '../../../lib/base_classes/ResizableComponent';

const RELEVANT_API_KEYS = [
  'watersewage', 'cleanpercent', 'squarefeet',
  'electricity_dollars', 'electricity_kwh', 'electricity_type',
  'naturalgas_dollars', 'naturalgas_therms', 'naturalgas_cuft', 'naturalgas_type',
  'heatingoil_dollars', 'heatingoil_gallons', 'heatingoil_type',
];

const MAX_SLIDER_WIDTH = 600;
const MIN_SLIDER_WIDTH = 250;

class HomeComponent extends ResizableComponent {

  constructor(props, context) {
    super(props, context);
    const home = this;
    home.initResizeListener();
    home.state = {
      width: window.innerWidth,
    };
  }

  componentDidMount() {
    const home = this;
    home.setDefaultUtilityProvider();
  }

  get slider_width() {
    let width = window.innerWidth * 0.8;
    width = Math.min(MAX_SLIDER_WIDTH, width);
    width = Math.max(MIN_SLIDER_WIDTH, width);
    return width;
  }

  get t() {
    return this.props.t;
  }

  get display_electricity_units() {
    if (this.unitsSet('electricity', 0)) return this.t('$/year');
    return this.t('kWh/year');
  }
  get display_naturalgas_units() {
    if (this.unitsSet('naturalgas', 0)) return this.t('$/year');
    else if (this.unitsSet('naturalgas', 1)) return this.t('therms/year');
    return this.t('cuft/year');
  }
  get display_heatingoil_units() {
    if (this.unitsSet('heatingoil', 0)) return this.t('$/year');
    return this.t('gallons/month');
  }
  get selected_utility_provider() {
    const selected = this.utility_provider_options.filter(item =>
       item.utility_id === this.state.utility_id);
    return selected[0].company;
  }
  get utility_provider_set() {
    return !!this.state.utility_id;
  }

  get api_key_base() {
    return 'input_footprint_housing';
  }

  apiKey(key_end) {
    return `${this.api_key_base}_${key_end}`;
  }

  get display_cleanpercent() {
    return userApiValue(this.apiKey('cleanpercent'), this.props.user_footprint, this.props.average_footprint);
  }

  get default_watersewage() {
    const { average_footprint } = this.props;
    return defaultApiValue(this.apiKey('watersewage'), average_footprint);
  }

  get display_annual_water_percentage() {
    const { user_footprint, average_footprint } = this.props;
    return parseInt(
      (userApiValue(this.apiKey('watersewage'), user_footprint, average_footprint) / this.default_watersewage) * 100,
      10,
    );
  }

  get relevant_api_keys() {
    return RELEVANT_API_KEYS;
  }

  get utility_provider_options() {
    try {
      const { user_footprint, average_footprint } = this.props;
      return JSON.parse(userApiValue('result_utility_providers', user_footprint, average_footprint));
    } catch (e) {
      // throw e;
    }
    return [];
  }

  get is_ontario() {
    return get(this.props.ui, 'widget.options.source') === 'ontario';
  }

  get waterHelpText() {
    return `
      ${this.t('Average household water consumption: 242 liters/ day')}
      <ul>
        <li>${this.t('1-person household: 148 liters/day')}</li>
        <li>${this.t('2-person household: 242 liters/day')}</li>
        <li>${this.t('3-person household: 261 liters/day')}</li>
        <li>${this.t('4-person household: 299 literes/day')}</li>
        <li>${this.t('5-person household: 337 liters/day')}</li>
      </ul>
    `;
  }

  displayRoundedValues(value) {
    return Math.round(value);
  }

  userCategoryInput(key_end) {
    const { user_footprint, average_footprint } = this.props;
    return userApiValue(this.apiKey(key_end), user_footprint, average_footprint);
  }

  defaultCategoryInput(key_end) {
    const home = this;
    const { average_footprint } = this.props;
    return Math.round(parseInt(defaultApiValue(home.apiKey(key_end), average_footprint), 10));
  }

  setDefaultUtilityProvider() {
    if (this.utility_provider_options.length > 0) {
      this.setUtiltyProvider(this.utility_provider_options[0].utility_id);
    }
  }

  setUtiltyProvider(utility_id) {
    const home = this;
    const utility = home.utility_provider_options.filter(item => item.utility_id === utility_id);
    const update = { input_footprint_housing_gco2_per_kwh: utility[0].weighted_emission_rate };
    this.props.computeUserFootprint(update);
    home.setState({ utility_id });
  }

  /*
   * Toggling Units
   */

  unitsSet(category, value) {
    const home = this;
    const api_key = home.apiKey(`${category}_type`);
    const { user_footprint, average_footprint } = this.props;
    const set_value = userApiValue(api_key, user_footprint, average_footprint);
    return parseInt(set_value, 10) === parseInt(value, 10);
  }

  setUnits(e) {
    e.preventDefault();
    const api_key = e.target.dataset.api_key;
    const update = { [api_key]: e.target.dataset.value };
    this.props.computeUserFootprint(update);
  }

  resize() {
    this.setState({ width: window.innerWidth });
  }

  render() {
    return (
      <div className="cc-component" id="home">
        <header className="cc-component__header">
          <span className="cc-component__byline">{this.t('How much do you use in your home?')}</span>
          <Help
            helpKey="home_page"
            t={s => this.t(s)}
          />
        </header>

        <div className="cc-component__form">
          <MultiUnitInput
            unitApiKey="input_footprint_housing_electricity_type"
            title={t => t('Electricity')}
          />
          {!this.is_ontario &&
            <div className="cc-component__question">
              <div className="selected-percentage">
                <div className="cc-component__question-label">{this.t('Percent purchased from a clean energy program')}: </div>
                <div className="label-container">
                  <span className="label label-slider">{this.display_cleanpercent} %</span>
                </div>
              </div>
              <Slider
                id="home_cleanpercent_slider"
                min={0}
                max={100}
                ticks={6}
                labels={[0, 20, 40, 60, 80, 100]}
                value={
                  userApiValue(this.apiKey('cleanpercent'), this.props.user_footprint, this.props.average_footprint)
                }
                onChange={
                  (cleanpercent) => {
                    this.props.computeUserFootprint({
                      [this.apiKey('cleanpercent')]: parseInt(cleanpercent, 10),
                    });
                  }
                }
              />
            </div>
          }
          {this.utility_provider_set &&
            <div className="cc-component__question utility-provider">
              <h4 className="title">{this.t('Utility Provider')}</h4>
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-default dropdown-toggle"
                  data-toggle="dropdown"
                >
                  {this.selected_utility_provider} <span className="caret" />
                </button>
                <ul className="dropdown-menu">
                  {this.utility_provider_options.map(provider => (
                    <li key={provider.utility_id}>
                      <a
                        href="#"
                        onClick={() => this.setUtiltyProvider(provider.utility_id)}
                      >
                        {provider.company}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          }
          <MultiUnitInput
            unitApiKey="input_footprint_housing_naturalgas_type"
            title={t => t('Natural Gas')}
          />
          <MultiUnitInput
            unitApiKey="input_footprint_housing_heatingoil_type"
            title={t => t('Heating Oil & Other Fuels')}
          />
          <HomeSize />

          <div className="cc-component__question">
            <h4>{this.t('Water Usage')}
              <CommonHelp
                popoverProps={{ id: 'water_help' }}
                helpText={this.waterHelpText}
              />
            </h4>
            <div className="label-container">
              <span className="label label-slider">
                {this.display_annual_water_percentage}
                <i>{this.t('% of similar households')}</i>
              </span>
            </div>
            <Slider
              id="home_watersewage_slider"
              min={0.01}
              max={3}
              step={0.01}
              ticks={4}
              labels={['0', '1x', '2x', '3x']}
              value={
                userApiValue(
                  this.apiKey('watersewage'), this.props.user_footprint,
                  this.props.average_footprint,
                ) / this.default_watersewage
              }
              onChange={
                (multiplier) => {
                  this.props.computeUserFootprint({
                    [this.apiKey('watersewage')]: multiplier * parseFloat(this.default_watersewage),
                  });
                }
              }
            />
          </div>
        </div>
        <NavButtons ui={this.props.ui} />
      </div>
    );
  }

}

const mapStateToProps = (state, ownProps) => bindSelectors(
  pick(selectors, [
    'user_footprint', 'average_footprint', 'ui',
  ]), state, ownProps,
);

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'computeUserFootprint',
  ]),
  dispatch,
);

export default wrap(
  withT('home'),
  connect(mapStateToProps, mapDispatchToProps),
  HomeComponent,
);
